/**
 * DonutChart — used for the invitation-status and RSVP-status breakdowns.
 *
 * Slice colours come from a fixed status→class map so the same colour means
 * the same thing across the dashboard (e.g. green for "published" everywhere).
 * Unknown statuses fall back to the muted on-surface-variant token.
 */

import * as React from 'react'
import type { CategoryCount } from '../types'

interface Props {
  data: CategoryCount[]
  /** Label rendered in the donut hole. */
  centerLabel?: string
  /** Optional sub-label below the centre number. */
  centerSubLabel?: string
  size?: number
  /** Maps status strings to tailwind text colour classes (currentColor → fill). */
  colorClass?: (status: string) => string
  /** Pretty label for a status — defaults to `Title Case`. */
  labelFor?: (status: string) => string
  ariaLabel?: string
}

const DEFAULT_COLORS: Record<string, string> = {
  PUBLISHED: 'text-emerald-500',
  SCHEDULED: 'text-amber-500',
  DRAFT: 'text-slate-400',
  PROCESSING: 'text-blue-400',
  FAILED: 'text-rose-500',
  EXPIRED: 'text-zinc-400',
  ARCHIVED: 'text-zinc-500',
  CANCELLED: 'text-rose-400',
  SUSPENDED: 'text-rose-600',
  REPORTED: 'text-rose-500',
  ACCEPTED: 'text-emerald-500',
  DECLINED: 'text-rose-400',
  MAYBE: 'text-amber-500',
}

const FALLBACK_COLOR = 'text-on-surface-variant'

export function DonutChart({
  data,
  centerLabel,
  centerSubLabel,
  size = 200,
  colorClass,
  labelFor,
  ariaLabel = 'Status distribution donut chart',
}: Props) {
  const total = data.reduce((s, d) => s + d.count, 0)
  const fmtLabel = labelFor ?? defaultLabelFor
  const colorFor = colorClass ?? ((s) => DEFAULT_COLORS[s] ?? FALLBACK_COLOR)

  const cx = size / 2
  const cy = size / 2
  const radius = size / 2 - 12
  const innerRadius = radius - 22

  let cursor = -Math.PI / 2 // 12 o'clock start

  const slices = data
    .filter((d) => d.count > 0)
    .map((d) => {
      const angle = (d.count / total) * Math.PI * 2
      const start = cursor
      const end = cursor + angle
      cursor = end
      return { ...d, start, end, color: colorFor(d.status) }
    })

  if (total === 0) {
    return (
      <div
        className="flex items-center justify-center text-on-surface-variant text-sm"
        style={{ height: size }}
      >
        No data yet.
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row items-center gap-6 w-full">
      <svg
        role="img"
        aria-label={ariaLabel}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="flex-shrink-0"
      >
        {slices.map((s, i) => (
          <path
            key={i}
            d={annularSliceD(cx, cy, radius, innerRadius, s.start, s.end)}
            className={s.color}
            fill="currentColor"
          >
            <title>{`${fmtLabel(s.status)}: ${s.count} (${Math.round((s.count / total) * 100)}%)`}</title>
          </path>
        ))}
        {centerLabel && (
          <text
            x={cx}
            y={cy - 2}
            textAnchor="middle"
            className="fill-on-surface font-bold"
            fontSize={22}
          >
            {centerLabel}
          </text>
        )}
        {centerSubLabel && (
          <text
            x={cx}
            y={cy + 18}
            textAnchor="middle"
            className="fill-on-surface-variant"
            fontSize={11}
          >
            {centerSubLabel}
          </text>
        )}
      </svg>

      <ul className="flex-1 grid grid-cols-1 gap-2 w-full">
        {slices.map((s, i) => (
          <li key={i} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <span className={`inline-block w-2.5 h-2.5 rounded-sm ${s.color}`} style={{ backgroundColor: 'currentColor' }} />
              <span className="text-on-surface">{fmtLabel(s.status)}</span>
            </span>
            <span className="text-on-surface-variant font-medium">
              {s.count.toLocaleString()}{' '}
              <span className="text-on-surface-variant/70">
                ({Math.round((s.count / total) * 100)}%)
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Build an SVG path for an annular sector. Handles >180° slices by using the
 * large-arc-flag — a common gotcha with naïve arc paths.
 */
function annularSliceD(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  start: number,
  end: number,
): string {
  const large = end - start > Math.PI ? 1 : 0
  const x1 = cx + rOuter * Math.cos(start)
  const y1 = cy + rOuter * Math.sin(start)
  const x2 = cx + rOuter * Math.cos(end)
  const y2 = cy + rOuter * Math.sin(end)
  const x3 = cx + rInner * Math.cos(end)
  const y3 = cy + rInner * Math.sin(end)
  const x4 = cx + rInner * Math.cos(start)
  const y4 = cy + rInner * Math.sin(start)
  return [
    `M ${x1.toFixed(2)} ${y1.toFixed(2)}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`,
    `L ${x3.toFixed(2)} ${y3.toFixed(2)}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${x4.toFixed(2)} ${y4.toFixed(2)}`,
    'Z',
  ].join(' ')
}

function defaultLabelFor(status: string): string {
  return status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
